/**
 * app/components/LegalAcceptanceModal.tsx
 * ---------------------------------------
 * What: Modal prompting users to accept the latest legal documents.
 * Why: Ensures compliance by gating features until acceptance is recorded.
 */
import React, { useState, useEffect } from 'react';
import { Modal, View, Text, TouchableOpacity, ScrollView, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
// import * as WebBrowser from 'expo-web-browser';
import DocumentViewerModal from './DocumentViewerModal';
import ThemedButton from './ThemedButton';
import { useLegal } from '../../lib/LegalContext';
import { useTheme } from '../../lib/ThemeContext';

// On web, when the modal first opens React Native Web automatically focuses the
// first *focusable* element which causes the browser to draw a default focus
// outline (a white box in dark mode) around the first checkbox. Visually this
// looks odd because it gives the impression that only the first checkbox is
// selected. We can safely hide the browser-specific outline without affecting
// accessibility because the component still receives focus and remains
// keyboard operable – we are only hiding the default ring. (Users relying on
// focus indicators will still see the accent coloured icon change when they
// toggle.)

// react-native-web allows any valid CSS property in the style object, but the
// TypeScript definitions don't include the outline properties. We therefore
// keep the style in an `any` typed constant.
const noOutlineStyle: any = {
  outline: 'none',
  outlineStyle: 'none',
  outlineWidth: 0,
  outlineColor: 'transparent',
  boxShadow: 'none',
};

export default function LegalAcceptanceModal() {
  const { pendingDocuments, acceptDocuments, loading, showModal, dismissModal } = useLegal();
  const { theme } = useTheme();
  const [checked, setChecked] = useState<string[]>([]);
  const [openDoc, setOpenDoc] = useState<string | null>(null);

  // Reset checks whenever the list changes (e.g., after acceptance)
  useEffect(() => {
    setChecked([]);
  }, [pendingDocuments]);

  if (loading || pendingDocuments.length === 0) return null;

  const toggleCheck = (id: string) => {
    setChecked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleAccept = async () => {
    try {
      await acceptDocuments(checked);
    } catch (err) {
      console.error('[LegalAcceptanceModal] Error accepting documents', err);
    }
  };

  const allChecked = checked.length === pendingDocuments.length;

  return (
    <>
    <Modal visible={showModal} transparent animationType="slide" onRequestClose={dismissModal}>
      <View style={[styles.overlay, { backgroundColor: theme.modalOverlay }]}>
        {/* Web-only style injection to hide default browser focus outline within modal */}
        {Platform.OS === 'web' && (
          // eslint-disable-next-line react/no-danger
          // @ts-ignore – HTML style tags only for web
          <style
            dangerouslySetInnerHTML={{
              __html: `
                /* Remove browser outline/box-shadow for any element that receives
                   focus inside the modal (focus-visible polyfill adds this attr) */
                [data-focus-visible-added] {
                  outline: none !important;
                  box-shadow: none !important;
                }
              `,
            }}
          />
        )}
        <View
          nativeID="legal-acceptance-modal"
          style={[styles.container, { backgroundColor: theme.card, borderColor: theme.border }]}
        >
          <Text style={[styles.title, { color: theme.text }]}>Please review and accept</Text>
          {/* Close button for deferring briefly */}
          <TouchableOpacity onPress={dismissModal} accessibilityLabel="Close">
            <Ionicons name="close" size={22} color={theme.text} />
          </TouchableOpacity>
          {/* Divider */}
          <View style={{ height: 1, backgroundColor: theme.border, marginBottom: 12 }} />
          <ScrollView style={{ flex: 1 }}>
            {pendingDocuments.map((doc) => (
              <View key={doc.id} style={styles.row}>
                <TouchableOpacity
                  onPress={() => toggleCheck(doc.id)}
                  style={[{ marginRight: 12 }, noOutlineStyle]}
                >
                  <Ionicons
                    name={checked.includes(doc.id) ? 'checkbox' : 'square-outline'}
                    size={24}
                    color={theme.accent}
                  />
                </TouchableOpacity>
                <Text style={[styles.label, { color: theme.text }]}>{labelForDocType(doc.doc_type)}</Text>
                <TouchableOpacity
                  onPress={() => setOpenDoc(doc.url)}
                  style={[{ marginLeft: 'auto' }, noOutlineStyle]}
                >
                  <Ionicons name="open-outline" size={22} color={theme.accent} />
                </TouchableOpacity>
              </View>
            ))}
          </ScrollView>
          <ThemedButton
            title="Accept"
            onPress={handleAccept}
            disabled={!allChecked}
            style={{ marginTop: 12 }}
            accessibilityLabel="Accept all documents"
          />
        </View>
      </View>
    </Modal>
    {/* In-app document viewer */}
    <DocumentViewerModal visible={!!openDoc} url={openDoc} onClose={() => setOpenDoc(null)} />
    </>
  );
}

function labelForDocType(type: string) {
  switch (type) {
    case 'terms':
      return 'Terms & Conditions';
    case 'privacy':
      return 'Privacy Policy';
    case 'gdpr_special':
      return 'GDPR Special Category Data Statement';
    default:
      return type;
  }
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    width: '90%',
    maxHeight: '80%',
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
  },
  title: {
    fontSize: 17,
    fontWeight: '500',
    marginBottom: 12,
    textAlign: 'center',
    fontFamily: 'Inter_500Medium',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    // @ts-ignore web only style props
    outlineWidth: 0,
    // @ts-ignore web only style props
    outlineStyle: 'none',
    // @ts-ignore web only style props
    outlineColor: 'transparent',
    // @ts-ignore web only style props
    boxShadow: 'none',
  },
  label: {
    flex: 1,
    fontSize: 16,
  },
}); 